import * as React from 'react';
import { SimpleCall, EPageTypes, EPlatformTypes } from '@cian/frontend-newbuilding-call-component';
import { ISimpleCall } from '@cian/frontend-newbuilding-call-component/es/types/simpleCallProps';
import { IHttpApi } from '@cian/http-api';
import { ILogger } from '@cian/logger';
import { useDispatch, useSelector } from 'react-redux';

import { setOpenedPhones } from '../../actions/openedPhones';
import { getNewbuildingId, getBuilders, getSellers } from '../../selectors/newbuilding';
import { getOpenedPhones } from '../../selectors/phones/getOpenedPhones';
import { IBuilder } from '../../types/newbuilding/builder';
import { ISeller } from '../../types/newbuilding/seller';
import { setUserOpenedPhoneInNewobject } from '../../utils/lastHopePopup';
import { Context } from '../../utils/withApplicationContext';
import { markAsOpen } from './utils/markAsOpen';

interface IShowPhoneParams {
  realtyUserId: number;
  phone: string;
}

type ISimpleCallProps = Pick<
  ISimpleCall,
  'onOpenContactsClick' | 'openContactsStyles' | 'showPhoneStyles' | 'SinglePhoneComponent'
>;

export interface ICallButtonProps extends ISimpleCallProps {
  placeType: string;
  onShowPhoneClick?(params: IShowPhoneParams): void;
}

export const CallButton = ({
  placeType,
  onShowPhoneClick,
  onOpenContactsClick,
  openContactsStyles,
  showPhoneStyles,
  SinglePhoneComponent,
}: ICallButtonProps) => {
  const context = React.useContext(Context);

  const newbuildingId = useSelector(getNewbuildingId);
  const opened = useSelector(getOpenedPhones)[placeType];
  const builders = useSelector(getBuilders);
  const sellers = useSelector(getSellers);
  const dispatch = useDispatch();

  const callBuilders = React.useMemo(
    () => (Array.isArray(opened) ? builders.map(seller => markAsOpen<IBuilder>({ seller, opened })) : builders),
    [builders, opened],
  );

  const callSellers = React.useMemo(
    () => (Array.isArray(opened) ? sellers.map(seller => markAsOpen<ISeller>({ seller, opened })) : sellers),
    [sellers, opened],
  );

  const handleShowPhoneClick = React.useCallback(
    (params: IShowPhoneParams) => {
      const { realtyUserId, phone } = params;
      const newOpened = [{ realtyUserId, phone }, ...(opened || [])];

      dispatch(setOpenedPhones({ [placeType]: newOpened }));
      setUserOpenedPhoneInNewobject(newbuildingId);

      if (onShowPhoneClick) {
        onShowPhoneClick(params);
      }
    },
    [newbuildingId, opened, onShowPhoneClick, placeType, dispatch],
  );

  const pageType = EPageTypes.NewbuildingCard;
  const platformType = EPlatformTypes.WebDesktop;

  return (
    <SimpleCall
      SinglePhoneComponent={SinglePhoneComponent}
      builders={callBuilders}
      sellers={callSellers}
      onOpenContactsClick={onOpenContactsClick}
      onShowPhoneClick={handleShowPhoneClick}
      openContactsStyles={openContactsStyles}
      showPhoneStyles={showPhoneStyles}
      httpApi={context?.httpApi as IHttpApi}
      logger={context?.logger as ILogger}
      pageType={pageType}
      platformType={platformType}
      placeType={placeType}
    />
  );
};

CallButton.displayName = 'CallButton';
